import { supabase } from '@/lib/supabaseClient';
import {
  deriveInvoiceStatus,
  parseContractorDate,
  updateJob,
  formatUsd,
  computeEmbeddedJobMargin,
} from '@/lib/jobsData';
import * as pqi from '@/lib/pqiTracker';

export { formatUsd };

/* ----------------------------------------------------------------------------
 * Load jobs + flatten invoices
 * ------------------------------------------------------------------------- */
const JOB_FIELDS =
  'id,name,client,client_id,address,contract,status,phase,invoices,created_at';

export const fetchJobsForInvoices = async () => {
  const { data, error } = await supabase
    .from('jobs')
    .select(JOB_FIELDS)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

/**
 * Flatten embedded jobs.invoices[] into top-level rows, each enriched with
 * job_id / job_name / client + derived status + date helpers.
 */
export const flattenInvoices = (jobs = []) => {
  const rows = [];
  for (const job of jobs) {
    const invoices = Array.isArray(job?.invoices) ? job.invoices : [];
    invoices.forEach((inv, idx) => {
      const status = deriveInvoiceStatus(inv);
      const dueDate = parseContractorDate(inv?.due);
      let daysPastDue = null;
      let daysUntilDue = null;
      if (dueDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const diff = Math.round((dueDate - today) / (1000 * 60 * 60 * 24));
        if (diff < 0) daysPastDue = Math.abs(diff);
        else daysUntilDue = diff;
      }
      rows.push({
        // Stable unique id for React keys: job_id + inv id + index
        row_key: `${job.id}-${inv?.id || 'noid'}-${idx}`,
        invoice_id: inv?.id || null,
        idx,
        job_id: job.id,
        job_name: job.name || 'Untitled job',
        job_status: job.status || '',
        job_phase: Number(job.phase) || 0,
        client: job.client || '',
        client_id: job.client_id || null,
        address: job.address || '',
        desc: inv?.desc || '',
        due: inv?.due || '',
        due_date: dueDate,
        amount: Number(inv?.amount) || 0,
        status,
        days_past_due: daysPastDue,
        days_until_due: daysUntilDue,
        raw: inv,
      });
    });
  }
  return rows;
};

/* ----------------------------------------------------------------------------
 * Stats
 * ------------------------------------------------------------------------- */
export const computeInvoiceStats = (rows = []) => {
  let total = 0;
  let collected = 0;
  let outstanding = 0;
  let overdue = 0;
  let dueSoon = 0;
  let upcoming = 0;
  const counts = { all: 0, paid: 0, overdue: 0, due: 0, upcoming: 0 };
  for (const r of rows) {
    total += r.amount;
    counts.all += 1;
    if (r.status === 'paid') {
      collected += r.amount;
      counts.paid += 1;
    } else {
      outstanding += r.amount;
      if (r.status === 'overdue') {
        overdue += r.amount;
        counts.overdue += 1;
      } else if (r.status === 'due') {
        dueSoon += r.amount;
        counts.due += 1;
      } else {
        upcoming += r.amount;
        counts.upcoming += 1;
      }
    }
  }
  return { total, collected, outstanding, overdue, dueSoon, upcoming, counts };
};

/* ----------------------------------------------------------------------------
 * Filter + sort
 * ------------------------------------------------------------------------- */
export const applyInvoiceFilter = (
  rows = [],
  { search = '', status = 'all', sort = 'due_soonest' } = {}
) => {
  const q = (search || '').trim().toLowerCase();
  let filtered = rows.filter((r) => {
    if (status !== 'all' && r.status !== status) return false;
    if (!q) return true;
    const hay = [r.job_name, r.client, r.desc, r.address]
      .map((v) => (v || '').toString().toLowerCase())
      .join(' | ');
    return hay.includes(q);
  });
  const bySoonest = (a, b) => {
    if (a.due_date && b.due_date) return a.due_date - b.due_date;
    if (a.due_date) return -1;
    if (b.due_date) return 1;
    return 0;
  };
  switch (sort) {
    case 'due_latest':
      filtered = filtered.slice().sort((a, b) => bySoonest(b, a));
      break;
    case 'amount_high':
      filtered = filtered.slice().sort((a, b) => b.amount - a.amount);
      break;
    case 'amount_low':
      filtered = filtered.slice().sort((a, b) => a.amount - b.amount);
      break;
    case 'job_az':
      filtered = filtered.slice().sort((a, b) =>
        (a.job_name || '').localeCompare(b.job_name || '')
      );
      break;
    case 'due_soonest':
    default:
      filtered = filtered.slice().sort(bySoonest);
      break;
  }
  return filtered;
};

/* ----------------------------------------------------------------------------
 * Write helpers — update embedded jobs.invoices[]
 * ------------------------------------------------------------------------- */
const patchInvoiceInJob = async (job, invoiceId, patch) => {
  if (!job?.id) throw new Error('Missing job id');
  const invoices = (Array.isArray(job.invoices) ? job.invoices : []).map((inv) =>
    inv?.id === invoiceId ? { ...inv, ...patch } : inv
  );
  return updateJob(job.id, { invoices });
};

export const markInvoicePaidById = async (job, invoiceId) => {
  const inv = (Array.isArray(job?.invoices) ? job.invoices : []).find(
    (i) => i?.id === invoiceId
  );
  const updated = await patchInvoiceInJob(job, invoiceId, { status: 'paid', paid_at: new Date().toISOString() });
  // PQI: track invoice payment (silent)
  pqi.trackInvoicePaid({ job, invoiceId, amount: Number(inv?.amount) || 0 });
  // PQI: margin snapshot — revenue collected posture changed
  {
    const { actualCost, totalRevenue, marginPct } = computeEmbeddedJobMargin(updated);
    pqi.trackMarginSnapshot({
      job: updated,
      actualCost,
      totalRevenue,
      marginPct,
      trigger: 'invoice_paid',
    });
  }
  return updated;
};

export const markInvoiceUnpaidById = async (job, invoiceId) =>
  patchInvoiceInJob(job, invoiceId, { status: 'upcoming', paid_at: null });
